/**
 * ─────────────────────────────────────────────
 * src/hooks/useKanban.js
 * ─────────────────────────────────────────────
 */

import { useDispatch, useSelector } from "react-redux";
import {
  fetchKanbanProjects,
  fetchKanbanBoard,
  createKanbanList,
  createKanbanTask,
  editKanbanTask,
  deleteKanbanTask,
  toggleKanbanTaskStatus,
  addKanbanSubtask,
  toggleKanbanSubtask,
  postKanbanComment,
  fetchProjectLogs,
  deleteProjectLogs,
  setKanbanSelectedProject,
  clearKanbanBoard,
  setKanbanColumns,
  clearKanbanErrors,
  clearProjectLogs,
} from "../store/slices/kanbanSlice";
import kanbanService from "../services/kanbanService";

const useKanban = () => {
  const dispatch = useDispatch();

  const {
    projects,
    selectedProject,
    columns,
    isProjectsLoading,
    isBoardLoading,
    isCreatingList,
    isCreatingTask,
    isEditingTask,
    isDeletingTask,
    isTogglingStatus,
    isAddingSubtask,
    isTogglingSubtask,
    isPostingComment,
    logs,
    logsProject,
    logsPagination,
    isLogsLoading,
    isDeletingLogs,
    projectsError,
    boardError,
    taskError,
    commentError,
    logsError,
  } = useSelector((state) => state.kanban);

  return {
    // ── State ──────────────────────────────
    projects,
    selectedProject,
    columns,

    // ── Loading ────────────────────────────
    isProjectsLoading,
    isBoardLoading,
    isCreatingList,
    isCreatingTask,
    isEditingTask,
    isDeletingTask,
    isTogglingStatus,
    isAddingSubtask,
    isTogglingSubtask,
    isPostingComment,
    isLogsLoading,
    isDeletingLogs,

    // ── Errors ─────────────────────────────
    projectsError,
    boardError,
    taskError,
    commentError,
    logsError,

    // ── Activity Logs state ────────────────
    logs,
    logsProject,
    logsPagination,

    // ── Actions ────────────────────────────
    loadProjects: () => dispatch(fetchKanbanProjects()),

    loadBoard: (projectId) => dispatch(fetchKanbanBoard(projectId)),

    selectProject: (project) => dispatch(setKanbanSelectedProject(project)),

    clearBoard: () => dispatch(clearKanbanBoard()),

    updateColumns: (cols) => dispatch(setKanbanColumns(cols)),

    addList: (projectId, name) =>
      dispatch(createKanbanList({ projectId, name })),

    addTask: (projectId, listId, taskData) =>
      dispatch(createKanbanTask({ projectId, listId, taskData })),

    updateTask: (projectId, listId, taskId, taskData) =>
      dispatch(editKanbanTask({ projectId, listId, taskId, taskData })),

    removeTask: (projectId, listId, taskId) =>
      dispatch(deleteKanbanTask({ projectId, listId, taskId })),

    toggleStatus: (projectId, listId, taskId) =>
      dispatch(toggleKanbanTaskStatus({ projectId, listId, taskId })),

    addSubtask: (projectId, listId, taskId, title) =>
      dispatch(addKanbanSubtask({ projectId, listId, taskId, title })),

    toggleSubtask: (projectId, listId, taskId, subtaskId) =>
      dispatch(toggleKanbanSubtask({ projectId, listId, taskId, subtaskId })),

    sendComment: (projectId, listId, taskId, payload) =>
      dispatch(postKanbanComment({ projectId, listId, taskId, payload })),

    updateComment: (projectId, listId, taskId, commentId, payload) =>
      kanbanService.updateComment(
        projectId,
        listId,
        taskId,
        commentId,
        payload,
      ),

    deleteComment: (projectId, listId, taskId, commentId) =>
      kanbanService.deleteComment(projectId, listId, taskId, commentId),

    clearErrors: () => dispatch(clearKanbanErrors()),

    updateSubtask: (projectId, listId, taskId, subtaskId, title) =>
      kanbanService.updateSubtask(projectId, listId, taskId, subtaskId, title),

    deleteSubtask: (projectId, listId, taskId, subtaskId) =>
      kanbanService.deleteSubtask(projectId, listId, taskId, subtaskId),

    addSubtaskAttachment: (projectId, listId, taskId, subtaskId, formData) =>
      kanbanService.addSubtaskAttachment(
        projectId,
        listId,
        taskId,
        subtaskId,
        formData,
      ),

    deleteSubtaskAttachment: (
      projectId,
      listId,
      taskId,
      subtaskId,
      attachmentId,
    ) =>
      kanbanService.deleteSubtaskAttachment(
        projectId,
        listId,
        taskId,
        subtaskId,
        attachmentId,
      ),

    loadProjectLogs: (projectId, page = 1) =>
      dispatch(fetchProjectLogs({ projectId, page })),

    removeProjectLogs: (projectId) => dispatch(deleteProjectLogs(projectId)),

    clearLogs: () => dispatch(clearProjectLogs()),
  };
};

export default useKanban;
